import logo from "../assets/spacem8-logo.png";
import { formatMoney as formatCurrency } from "./currency";
import { supabase } from "./supabase";
import { buildStorageReference } from "./storageReferences";

export const TENANT_IMAGE_BUCKET = "tenant-images";
export const TENANT_DOCUMENT_BUCKET = "tenant-documents";

export function formatMoney(amount, currency) {
  return formatCurrency(amount, currency);
}

export function getStatusClasses(status) {
  if (status === "Active") return "bg-emerald-100 text-emerald-700";
  if (status === "Notice") return "bg-sky-100 text-sky-700";
  return "bg-amber-100 text-amber-800";
}

export function parseDocumentLinks(value) {
  return String(value || "")
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);
}

export function getDaysPastDue(dueDate) {
  if (!dueDate) return 0;

  const due = new Date(`${dueDate}T00:00:00`);
  if (Number.isNaN(due.getTime())) return 0;

  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const diff = startOfToday.getTime() - due.getTime();
  return Math.max(0, Math.floor(diff / 86400000));
}

export function invoiceStatusClasses(status) {
  if (status === "Paid") return "bg-emerald-100 text-emerald-700";
  if (status === "Partial") return "bg-sky-100 text-sky-700";
  if (status === "Overdue") return "bg-rose-100 text-rose-700";
  return "bg-amber-100 text-amber-800";
}

export function paymentStatusClasses(status) {
  if (status === "Paid") return "bg-emerald-100 text-emerald-700";
  if (status === "Pending") return "bg-sky-100 text-sky-700";
  return "bg-rose-100 text-rose-700";
}

export function buildTenantAccountSnapshot(tenant, accountData) {
  const documents = parseDocumentLinks(tenant?.supporting_documents);
  const payments = accountData?.payments || [];
  const invoices = accountData?.invoices || [];
  const paidByInvoice = payments.reduce((totalsByInvoice, payment) => {
    if (payment.invoice_id && payment.status === "Paid") {
      totalsByInvoice[payment.invoice_id] =
        (totalsByInvoice[payment.invoice_id] || 0) + Number(payment.amount || 0);
    }
    return totalsByInvoice;
  }, {});

  const invoiceLedger = invoices.map((invoice) => {
    const amount = Number(invoice.amount || 0);
    const paidAmount =
      invoice.status === "Paid" && !paidByInvoice[invoice.id]
        ? amount
        : Number(paidByInvoice[invoice.id] || 0);
    const balance = Math.max(amount - paidAmount, 0);
    const daysPastDue = getDaysPastDue(invoice.due_date);

    return {
      ...invoice,
      amount,
      paidAmount,
      balance,
      daysPastDue,
    };
  });

  const accountSummary = {
    invoiced: invoiceLedger.reduce((sum, invoice) => sum + invoice.amount, 0),
    paid: payments
      .filter((payment) => payment.status === "Paid")
      .reduce((sum, payment) => sum + Number(payment.amount || 0), 0),
    balance: invoiceLedger.reduce((sum, invoice) => sum + invoice.balance, 0),
    overdue: invoiceLedger
      .filter((invoice) => invoice.balance > 0 && invoice.daysPastDue > 0)
      .reduce((sum, invoice) => sum + invoice.balance, 0),
  };

  return {
    documents,
    invoiceLedger,
    accountSummary,
  };
}

export function getTenantProfileItems(tenant, currency) {
  return [
    {
      label: "Property",
      value: tenant.property_name || "Not set",
    },
    {
      label: "Unit",
      value: tenant.unit || "Not set",
    },
    {
      label: "Phone",
      value: tenant.phone || "No phone",
    },
    {
      label: "Email",
      value: tenant.email || "No email",
    },
    {
      label: "Monthly rent",
      value: formatMoney(Number(tenant.monthly_rent || 0), currency),
    },
    {
      label: "Lease start",
      value: tenant.lease_start || "Not set",
    },
    {
      label: "Lease end",
      value: tenant.lease_end || "Not set",
    },
    {
      label: "Tenant ID",
      value: tenant.id || "Not set",
    },
  ];
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function safeFilename(value) {
  return String(value || "tenant-profile").replace(/[^a-zA-Z0-9._-]/g, "-");
}

function getLogoUrl() {
  if (/^https?:\/\//i.test(logo)) return logo;
  return `${window.location.origin}${logo.startsWith("/") ? "" : "/"}${logo}`;
}

function getTenantStatementHtml(tenant, summary, invoices, payments, documents, currency) {
  const generatedDate = new Date().toISOString().slice(0, 10);
  const logoUrl = getLogoUrl();
  const invoiceRows = invoices.length
    ? invoices
        .map(
          (invoice) => `
        <tr>
          <td><strong>${escapeHtml(invoice.id)}</strong><br><span>${escapeHtml(invoice.status)}</span></td>
          <td>${escapeHtml(invoice.due_date || "Not set")}</td>
          <td class="right">${escapeHtml(formatMoney(invoice.amount, currency))}</td>
          <td class="right">${escapeHtml(formatMoney(invoice.paidAmount, currency))}</td>
          <td class="right">${escapeHtml(formatMoney(invoice.balance, currency))}</td>
          <td class="right">${escapeHtml(invoice.balance > 0 ? invoice.daysPastDue : 0)}</td>
        </tr>`
        )
        .join("")
    : `<tr><td colspan="6" class="empty">No invoices found.</td></tr>`;
  const paymentRows = payments.length
    ? payments
        .map(
          (payment) => `
        <tr>
          <td><strong>${escapeHtml(payment.id)}</strong><br><span>${escapeHtml(payment.invoice_id || "Unlinked")}</span></td>
          <td>${escapeHtml(payment.due_date || payment.created_at?.slice(0, 10) || "Not set")}</td>
          <td>${escapeHtml(payment.payment_method || "Not set")}</td>
          <td>${escapeHtml(payment.status)}</td>
          <td class="right">${escapeHtml(formatMoney(Number(payment.amount || 0), currency))}</td>
        </tr>`
        )
        .join("")
    : `<tr><td colspan="5" class="empty">No payments found.</td></tr>`;
  const documentRows = documents.length
    ? documents
        .map(
          (documentUrl, index) => `
        <li>Document ${index + 1}: ${escapeHtml(documentUrl)}</li>`
        )
        .join("")
    : "<li>No supporting documents added.</li>";

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(tenant.full_name)} Statement</title>
  <style>
    body { margin: 0; background: #f8fafc; color: #0f172a; font-family: Arial, sans-serif; }
    .statement { max-width: 920px; margin: 28px auto; background: #fff; padding: 42px; border: 1px solid #e2e8f0; }
    .top { display: flex; justify-content: space-between; gap: 28px; border-bottom: 2px solid #0f172a; padding-bottom: 22px; }
    .brand { display: flex; align-items: center; gap: 14px; }
    .brand img { width: 76px; height: auto; object-fit: contain; }
    h1 { margin: 0; font-size: 28px; letter-spacing: 0.04em; }
    h2 { margin: 28px 0 10px; font-size: 15px; text-transform: uppercase; letter-spacing: 0.12em; color: #64748b; }
    p { margin: 4px 0; line-height: 1.45; }
    .muted, span { color: #64748b; }
    .right { text-align: right; }
    .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 22px; margin-top: 26px; }
    .box { border: 1px solid #e2e8f0; background: #f8fafc; padding: 16px; }
    .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-top: 24px; }
    .summary strong { display: block; margin-top: 8px; font-size: 18px; }
    table { width: 100%; border-collapse: collapse; margin-top: 10px; }
    th { background: #f1f5f9; color: #475569; text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; padding: 11px; }
    td { border-bottom: 1px solid #e2e8f0; padding: 12px 11px; vertical-align: top; font-size: 13px; }
    ul { margin: 10px 0 0; padding-left: 18px; color: #475569; font-size: 13px; line-height: 1.6; }
    .empty { color: #64748b; text-align: center; }
    .footer { margin-top: 34px; border-top: 1px solid #e2e8f0; padding-top: 16px; color: #64748b; font-size: 12px; }
    @media print { body { background: #fff; } .statement { margin: 0; max-width: none; border: 0; } }
  </style>
</head>
<body>
  <main class="statement">
    <section class="top">
      <div class="brand">
        <img src="${escapeHtml(logoUrl)}" alt="SpaceM8" />
        <div>
          <h1>TENANT STATEMENT</h1>
          <p class="muted">SpaceM8 Property Management</p>
        </div>
      </div>
      <div class="right">
        <p><strong>${escapeHtml(tenant.full_name)}</strong></p>
        <p>Generated: ${escapeHtml(generatedDate)}</p>
        <p>Status: ${escapeHtml(tenant.status)}</p>
      </div>
    </section>

    <section class="grid">
      <div class="box">
        <h2>Tenant</h2>
        <p><strong>${escapeHtml(tenant.full_name)}</strong></p>
        <p>${escapeHtml(tenant.email || "No email")}</p>
        <p>${escapeHtml(tenant.phone || "No phone")}</p>
      </div>
      <div class="box">
        <h2>Lease</h2>
        <p>${escapeHtml(tenant.property_name || "Not set")}, Unit ${escapeHtml(tenant.unit || "Not set")}</p>
        <p>Rent: ${escapeHtml(formatMoney(Number(tenant.monthly_rent || 0), currency))}</p>
        <p>${escapeHtml(tenant.lease_start || "No start date")} to ${escapeHtml(tenant.lease_end || "No end date")}</p>
      </div>
    </section>

    <section class="summary">
      <div class="box"><span>Invoiced</span><strong>${escapeHtml(formatMoney(summary.invoiced, currency))}</strong></div>
      <div class="box"><span>Paid</span><strong>${escapeHtml(formatMoney(summary.paid, currency))}</strong></div>
      <div class="box"><span>Balance</span><strong>${escapeHtml(formatMoney(summary.balance, currency))}</strong></div>
      <div class="box"><span>Overdue</span><strong>${escapeHtml(formatMoney(summary.overdue, currency))}</strong></div>
    </section>

    <h2>Invoice History</h2>
    <table>
      <thead>
        <tr>
          <th>Invoice</th>
          <th>Due</th>
          <th class="right">Amount</th>
          <th class="right">Paid</th>
          <th class="right">Balance</th>
          <th class="right">Days</th>
        </tr>
      </thead>
      <tbody>${invoiceRows}</tbody>
    </table>

    <h2>Payment History</h2>
    <table>
      <thead>
        <tr>
          <th>Payment</th>
          <th>Date</th>
          <th>Method</th>
          <th>Status</th>
          <th class="right">Amount</th>
        </tr>
      </thead>
      <tbody>${paymentRows}</tbody>
    </table>

    <h2>Supporting Documents</h2>
    <ul>${documentRows}</ul>

    <p class="footer">Generated from SpaceM8 tenant profile. Review linked invoices and payments for detailed source records.</p>
  </main>
</body>
</html>`;
}

export function printTenantStatement(
  tenant,
  summary,
  invoices,
  payments,
  documents,
  currency,
  onBlocked = () => {}
) {
  const printWindow = window.open("", "_blank", "width=950,height=720");
  if (!printWindow) {
    onBlocked();
    return;
  }

  printWindow.document.write(getTenantStatementHtml(tenant, summary, invoices, payments, documents, currency));
  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
}

export function downloadTenantStatement(tenant, summary, invoices, payments, documents, currency) {
  const blob = new Blob([getTenantStatementHtml(tenant, summary, invoices, payments, documents, currency)], {
    type: "text/html;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = `${safeFilename(tenant.full_name)}-tenant-statement.html`;
  link.click();
  URL.revokeObjectURL(url);
}

function getUploadPath(tenantId, file) {
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "-");
  const owner = tenantId || "new-tenant";

  return `${owner}/${Date.now()}-${safeName}`;
}

export async function uploadTenantFile(bucket, tenantId, file) {
  const path = getUploadPath(tenantId, file);
  const { error } = await supabase.storage.from(bucket).upload(path, file, {
    cacheControl: "3600",
    upsert: false,
  });

  if (error) throw error;

  return buildStorageReference(bucket, path);
}
